// ============================================================
// A BROTHER'S SQUAD MODE PLATES ON THE DRUM (Squad Mode stage 7, canvas
// board 9): Squad Mode (enter, a Full Action), the Cohesion Challenge (a
// Free Action), Solo Mode (leave, free), then the Oath's abilities - start
// one at its action, join one running for free. Each dimmed with its
// reason, never hidden (ruled), and the run's length fixed for the brother
// (the three, then one per ability): a plate's place on the cylinder is its
// index, and E2E navigates by it (gotcha 10).
//
// APPENDED after his own stances, for a brother of a kill-team with an Oath only:
// the catalog's plates never move.
//
// Pure. Zero React dependency.
// ============================================================
import type { ActionDef } from './types';
import type { AbilityPlate } from '../killTeam/strip';

/** What a Squad Mode plate's id starts with: no catalog row (ids have no colon) nor a stance (`own:`) shares it. */
const KILL_TEAM_PLATE_PREFIX = 'kt:';

interface Facts {
  inSquad: boolean;
  /** Waiting to enter at his next turn: Solo Mode calls it off. */
  pending?: boolean;
  /** Why he cannot enter now; null when he can. */
  enterWhy: string | null;
  plates: readonly AbilityPlate[];
}

const plate = (
  id: string,
  name: string,
  cost: ActionDef['cost'],
  killTeam: ActionDef['killTeam'],
  blurb: string,
): ActionDef => ({
  id: `${KILL_TEAM_PLATE_PREFIX}${id}`,
  name,
  cost,
  category: 'utility',
  execution: 'killTeam',
  section: 'squad',
  requiresActor: true,
  killTeam,
  blurb,
});

/** Why each plate of the run is dimmed, by id. */
function reasonsOf(f: Facts): Record<string, string> {
  const reasons: Record<string, string> = {};
  const entry = f.inSquad ? 'Already in Squad Mode.' : f.enterWhy;
  if (entry) reasons[`${KILL_TEAM_PLATE_PREFIX}enter`] = reasons[`${KILL_TEAM_PLATE_PREFIX}challenge`] = entry;
  if (!f.inSquad && !f.pending) reasons[`${KILL_TEAM_PLATE_PREFIX}leave`] = 'Not in Squad Mode.';
  for (const p of f.plates) if (p.why) reasons[`${KILL_TEAM_PLATE_PREFIX}${p.id}`] = p.why;
  return reasons;
}

/** The run of plates, and why each is dimmed (by id). */
export function killTeamDrum(f: Facts): { defs: ActionDef[]; reasons: Record<string, string> } {
  const defs = [
    plate('enter', 'Squad Mode', 'full', { verb: 'enter' }, 'Enter Squad Mode beside a brother in support range.'),
    plate(
      'challenge',
      'Cohesion Challenge',
      'free',
      { verb: 'challenge' },
      'A d10 at or under the Cohesion left: in at once.',
    ),
    plate('leave', 'Solo Mode', 'free', { verb: 'leave' }, 'Leave Squad Mode.'),
    ...f.plates.map((p) =>
      p.kind === 'join'
        ? plate(
            p.id,
            `${p.name} (join)`,
            'free',
            { verb: 'join', abilityId: p.id, key: p.key ?? undefined },
            'Join it: free.',
          )
        : plate(
            p.id,
            p.name,
            p.action,
            { verb: 'start', abilityId: p.id, cost: p.cost },
            `Start it: ${p.cost} Cohesion.`,
          ),
    ),
  ];
  return { defs, reasons: reasonsOf(f) };
}

/** The ids lit on the drum: Squad Mode while he is in it, and every ability he has. */
export function killTeamEngaged(f: Facts): string[] {
  return [
    ...(f.inSquad ? [`${KILL_TEAM_PLATE_PREFIX}enter`] : []),
    ...f.plates.filter((p) => p.pressed).map((p) => `${KILL_TEAM_PLATE_PREFIX}${p.id}`),
  ];
}
